import * as css from 'css/exports.module.scss';
import './NotificationCard.scss';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { getExport } from '@sonar-software/sonar-core';
import { getInitiativeRoute } from '@lib/routes';
import { getNotificationDisplayTime } from './@arch/lib';
import { SanitizedHtml } from '../general';
import { useNavigate } from 'react-router-dom';
import { When } from 'react-if';
import { Badge, Button, Card, Row, Tooltip, Typography } from 'antd';
import { COPY_GENERIC, FULL_ROUTES, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { notificationsActions, SonarNotificationType } from '@sonar-software/sonar-notifications';
import React, { useCallback, useMemo } from 'react';
import { useAction, useDownload } from '@hooks';
function getNotificationRoute(type, referenceId) {
    switch (type) {
        case SonarNotificationType.InitiativeLinkedJiraIssueDeleted:
        case SonarNotificationType.InitiativeCommentMention:
            return getInitiativeRoute(referenceId);
        case SonarNotificationType.JiraIntegration:
            return FULL_ROUTES.SETTINGS_JIRA;
        case SonarNotificationType.IntegrationDown:
        case SonarNotificationType.NewApplication:
        case SonarNotificationType.NewFieldOrObjectAccess:
        case SonarNotificationType.NewFieldByConfigurableCriteriaAccessAlert:
        case SonarNotificationType.NewUser:
            return referenceId ? `${FULL_ROUTES.DATA_SECURITY}/${referenceId}` : '';
        default:
            return '';
    }
}
function getButtonText(type) {
    switch (type) {
        case SonarNotificationType.AccessLogExportReady:
        case SonarNotificationType.ApiVolumeTableExportReady:
        case SonarNotificationType.DataDictionaryExportReady:
        case SonarNotificationType.InitiativeExportReady:
        case SonarNotificationType.AccessChangeLogExportReady:
        case SonarNotificationType.IncidentLogExportReady:
        case SonarNotificationType.TimelineExportReady:
        case SonarNotificationType.SalesforceBulkPatchSummaryReady:
        case SonarNotificationType.SetupAuditTrailExportReady:
            return COPY_GENERIC.DOWNLOAD_CSV;
        default:
            return '';
    }
}
export const NotificationCard = ({ notification }) => {
    const navigate = useNavigate();
    const dismiss = useAction(notificationsActions.requestDeleteNotificationsById);
    const markRead = useAction(notificationsActions.requestPatchNotificationsById);
    const download = useDownload();
    const { notificationId, description, isRead, referenceId, timeStamp, title, notificationType } = notification;
    const displayTime = useMemo(() => getNotificationDisplayTime(timeStamp), [timeStamp]);
    const handleDismissClick = useCallback((e) => {
        e.stopPropagation();
        dismiss({
            id: notificationId,
            type: notificationType
        });
    }, [dismiss, notificationId, notificationType]);
    const buttonText = getButtonText(notificationType);
    const handleClick = useCallback(() => {
        const route = getNotificationRoute(notificationType, referenceId);
        markRead({
            id: notificationId,
            type: notificationType
        });
        if (route) {
            navigate(route);
        }
        if (buttonText === COPY_GENERIC.DOWNLOAD_CSV) {
            download({
                errorMessage: NOTIFICATIONS_COPY.ERROR_DOWNLOAD_EXPORT,
                request: () => getExport({ exportId: referenceId })
            });
        }
    }, [buttonText, download, markRead, navigate, notificationId, notificationType, referenceId]);
    const notificationClassNames = classNames('notification-card', { unread: !isRead });
    return (React.createElement(Card, { className: notificationClassNames, extra: React.createElement(Tooltip, { overlay: COPY_GENERIC.DISMISS },
            React.createElement(CloseOutlined, { onClick: handleDismissClick })), onClick: handleClick, title: React.createElement(React.Fragment, null,
            React.createElement("div", { className: 'h3' }, title),
            React.createElement(When, { condition: !isRead },
                React.createElement(Badge, { color: css.badgeColor }))) },
        React.createElement(Typography.Paragraph, { className: 'notification-message', ellipsis: {
                rows: 3
            } },
            React.createElement(SanitizedHtml, { html: description })),
        React.createElement(Row, { justify: 'space-between' },
            React.createElement("div", { className: 'notification-timestamp' }, displayTime),
            React.createElement(When, { condition: buttonText },
                React.createElement(Button, { type: 'primary' }, buttonText)))));
};
